import { faFloppyDisk } from '@fortawesome/pro-regular-svg-icons';
import { Group, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { AdTagCreative, DisplayCreative } from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import {
  Button,
  ContextMenu,
  FullScreenModal,
  Icon,
  Pill,
  useVideoFrameExtract,
} from '@feathr/components';

import CreativeForm from './CreativeForm';
import PreviewPanel from './PreviewPanel';
import type { IThumbnailCarouselItem } from './ThumbnailCarousel';
import ThumbnailCarousel, { DEFAULT_VISIBLE_COUNT } from './ThumbnailCarousel';

import * as styles from './CreativeModal.css';

export interface ICreativeModalProps {
  creative?: DisplayCreative;
  /** Array of thumbnail items to display in the carousel. */
  thumbnailItems?: IThumbnailCarouselItem[];
  opened: boolean;
  onClose: () => void;
  /** Callback when a thumbnail is selected in the carousel. */
  onSelectCreative?: (id: string) => void;
}

function CreativeModal({
  creative,
  thumbnailItems,
  opened,
  onClose,
  onSelectCreative,
}: Readonly<ICreativeModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const isAdTag = creative?.get('_cls') === CreativeClass.DisplayAdTag;
  const adTagHtml = isAdTag ? (creative as AdTagCreative).get('adtag') : undefined;
  const creativeUrl = isAdTag ? undefined : creative?.get('url');
  const isVideo = creative?.creativeType === 'Video';
  const {
    thumbnailUrl: videoFrameUrl,
    isLoading: isVideoThumbnailLoading,
    hasError: hasVideoThumbnailError,
  } = useVideoFrameExtract(creativeUrl ?? '', isVideo && !!creativeUrl);

  const previewImageUrl = isVideo ? videoFrameUrl || undefined : creativeUrl;

  const title = (
    <Group gap={theme.spacing['2']}>
      <span>{t('Preview creative')}</span>
      <Pill>{creative?.creativeType ?? t('Image')}</Pill>
    </Group>
  );

  const leftActions =
    thumbnailItems && onSelectCreative ? (
      <div className={styles.carouselWrapper}>
        <ThumbnailCarousel
          items={thumbnailItems}
          onSelect={onSelectCreative}
          selectedId={creative?.id ?? ''}
          visibleCount={Math.min(thumbnailItems.length, DEFAULT_VISIBLE_COUNT)}
        />
      </div>
    ) : undefined;

  const rightActions = (
    <>
      <ContextMenu iconName={'ellipsis'} name={'creative-modal-actions'}>
        <ContextMenu.Item>{t('Placeholder action')}</ContextMenu.Item>
      </ContextMenu>
      <Button
        name={'creative-modal-save'}
        onClick={onClose}
        prefix={<Icon icon={faFloppyDisk} />}
        type={'primary'}
      >
        {t('Save and close')}
      </Button>
    </>
  );

  return (
    <FullScreenModal
      className={styles.modal}
      closeOnEscape={false}
      leftActions={leftActions}
      onClose={onClose}
      opened={opened}
      rightActions={rightActions}
      theme={'flush'}
      title={title}
    >
      <div className={styles.body}>
        <div className={styles.previewArea}>
          <PreviewPanel
            adTagHtml={adTagHtml}
            contentHeight={creative?.get('height')}
            contentWidth={creative?.get('width')}
            creativeName={creative?.name}
            hasVideoThumbnailError={isVideo && hasVideoThumbnailError}
            imageUrl={previewImageUrl}
            isAdTag={isAdTag}
            isMetaCreative={creative?.isFacebook}
            isVideoThumbnailLoading={isVideo && isVideoThumbnailLoading && !hasVideoThumbnailError}
          />
        </div>
        <div className={styles.sidebar}>{creative && <CreativeForm creative={creative} />}</div>
      </div>
    </FullScreenModal>
  );
}

export default CreativeModal;
